import { Injectable } from "@angular/core";
import { ApiResource } from "@auvious/common";
import { AuviousRtcService } from "./rtc.service";
import {
  AppointmentEventType,
  AppointmentParticipantStateEnum,
  AppointmentViewState,
  IAppointmentContact,
  IAppointmentNotificationLog,
  IAppointmentNotificationLogEvent,
  IAppointmentParticipantBase,
  IAppointmentRouting,
  IContactBase,
} from "../models/Appointment";
import {
  PageableRequest,
  IPagedResponse,
  AppointmentLogRequestBuilder,
  AppointmentLogEventRequestBuilder,
} from "../models/Pageable";
import { Subject, BehaviorSubject, merge, filter } from "rxjs";
import { IAppointmentLocale } from "../models/Appointment";
import {
  IAppointmentNotification,
  IAppointmentFormat,
} from "../models/Appointment";
import {
  IAppointment,
  IScheduleAppointmentRequest,
  IScheduleAppointmentOptions,
  IScheduledAppointmentContact,
} from "../models/Appointment";
import { GenericErrorHandler } from "./error-handlers.service";
import { ApplicationService } from "./application.service";
import {
  IWebhookSubscription,
  IWebhookSubscriptionBasicAuth,
  IWebhookSubscriptionClientCredentials,
} from "../models/Webhooks";
import {
  AppointmentNotificationTypeEnum,
  AppointmentWebhookNotificationTypeEnum,
  ConversationOriginEnum,
  ScheduleTypeEnum,
  TerminateReasonEnum,
  WebhookSubscriptionAuthenticationEnum,
} from "../core-ui.enums";
import { debug, debugError } from "./utils";
import moment from "moment";
import { UserService } from "./user.service";
import { IntegrationService, InteractionService } from "../../app/services";
import { AppConfigService } from "./app.config.service";
import { AgentParam, IInteraction } from "../models";
import { IConferenceSession } from "@auvious/rtc";
import { Router } from "@angular/router";

@Injectable({
  providedIn: "root",
})
export class AppointmentService {
  private tagFormats: IAppointmentFormat[] = [];
  private supportedLocales: IAppointmentLocale[] = [];
  private webhookTypes: AppointmentWebhookNotificationTypeEnum[] = [];

  private appointmentResource: ApiResource;
  private notificationResource: ApiResource;

  // this should be behavioursubject because as a customer,
  // it is being set by the guard and then read by the appointment page
  // this is also used in CRUD operations while scheduling
  private _appointmentChanged = new BehaviorSubject<
    IAppointment<IAppointmentRouting>
  >(undefined);
  public appointmentChanged$ = this._appointmentChanged.asObservable();

  private _appointmentCreated = new Subject<string>();
  public appointmentCreated$ = this._appointmentCreated.asObservable();

  private _appointmentStateChanged = new Subject<{
    appointmentId: string;
    type: AppointmentEventType;
  }>();
  public appointmentStateChanged$ =
    this._appointmentStateChanged.asObservable();

  private _appointmentEnded = new Subject<IAppointment<IAppointmentRouting>>();
  public appointmentEnded$ = this._appointmentEnded.asObservable();

  private _appointmentParticipantStateChanged = new Subject<{
    appointmentId: string;
    type: AppointmentEventType;
  }>();
  public appointmentParticipantStateChanged$ =
    this._appointmentParticipantStateChanged.asObservable();

  private _appointmentContacts = new BehaviorSubject<
    Map<string, IScheduledAppointmentContact>
  >(new Map());
  appointmentContacts$ = this._appointmentContacts.asObservable();

  private _appointmentContactsMap: Map<string, IScheduledAppointmentContact> =
    new Map();

  private isAlreadyListening = false;
  private endingInteraction: IInteraction;
  private completedAppointmentInteractions: {
    [interactionId: string]: boolean;
  } = {};

  constructor(
    private rtcService: AuviousRtcService,
    private errorHandler: GenericErrorHandler,
    private application: ApplicationService,
    private interaction: InteractionService,
    private integration: IntegrationService,
    private user: UserService,
    private config: AppConfigService,
    private router: Router
  ) {
    this.rtcService.common$.subscribe((common) => {
      this.appointmentResource = common.apiResourceFactory(
        "api/v2/appointments"
      );
      this.notificationResource =
        common.apiResourceFactory("api/notifications");
    });
  }

  public setAppointmentAsCompletedInInteraction(interactionId: string) {
    this.completedAppointmentInteractions = {
      ...this.completedAppointmentInteractions,
      [interactionId]: true,
    };
  }

  /**
   * caches an appointment participant locally
   * @param participant
   */
  public registerAppointmentParticipant(
    participant: IScheduledAppointmentContact
  ) {
    this._appointmentContactsMap.set(participant.id, participant);
  }

  /**
   * caches appointment participants locally. If not found locally, they are retrieved from the service
   * @param appointmentId
   * @param participants
   */
  public async requestAppointmentParticipants(
    appointmentId: string,
    participants: { id: string }[]
  ) {
    participants.forEach(async (p) => {
      if (this._appointmentContactsMap.has(p.id)) return;

      this._appointmentContactsMap.set(p.id, undefined);

      const contact = await this.getParticipantDetails(appointmentId, p.id);
      this._appointmentContactsMap.set(p.id, contact);

      this._appointmentContacts.next(this._appointmentContactsMap);
    });

    // const results = await Promise.all(
    //   Array.from(this.organizerMap.entries())
    //     .filter(([id, contact]) => !contact)
    //     .map(async ([id]) => {
    //       try {
    //         const a = this.organizerAppointmentMap.get(id);
    //         const contact = await this.appointmentService.getParticipantDetails(
    //           a,
    //           id
    //         );
    //         return { id, contact };
    //       } catch (ex) {
    //         return { id, contact: undefined };
    //       }
    //     })
    // );
    // results.forEach((r) => this.organizerMap.set(r.id, r.contact));
    // this._organizers.next(this.organizerMap);
    // }
  }

  public listenForEndedInteractions() {
    // don't even bother if no appointments or interactions supported
    if (!this.isAppointmentInteractionAvailable || this.isAlreadyListening)
      return;

    this.isAlreadyListening = true;

    // in genesys we get the ending/ended events
    // in talkdesk we only get the ended event
    merge(
      this.interaction.eventClient?.ending$,
      this.interaction.eventClient?.ended$
    )
      .pipe(
        filter((id) => {
          const inter = this.interaction.getActiveInteraction();

          return (
            !!inter &&
            !this.completedAppointmentInteractions[id] && // do not double end
            inter.getId() === id &&
            inter.getOrigin() == ConversationOriginEnum.APPOINTMENT
          );
        })
      )
      .subscribe(async (id) => {
        this.endingInteraction = this.interaction.getActiveInteraction();

        // complete the appointment
        const app = await this.complete(
          this.endingInteraction.getAppointmentId()
        );
        this.setAppointmentAsCompletedInInteraction(id);
        this._appointmentEnded.next(app);
      });

    // terminate conference
    this.interaction.eventClient?.ended$
      .pipe(
        filter((conversationId) => {
          const inter =
            this.endingInteraction || // in genesys we will have it from ending$
            this.interaction.getActiveInteraction(); // in talkdesk we only have it here
          return (
            !this.isInConference && // conference handles for terminate session
            !!inter &&
            !!inter.getRoom() && // if the agent terminates the call, we don't have a room
            inter.getId() === conversationId &&
            inter.getOrigin() == ConversationOriginEnum.APPOINTMENT
          );
        })
      )
      .subscribe(async (_) => {
        const intr =
          this.endingInteraction || this.interaction.getActiveInteraction();

        // join the conference to terminate it
        this.rtcService.client.joinConference({
          id: intr.getRoom(),
          eventHandlers: {
            accepted: (session: IConferenceSession) => {
              setTimeout(() => {
                session.terminate(TerminateReasonEnum.appointmentEnded);
              }, 500);
            },
          },
        });

        this.endingInteraction = undefined;
      });
  }

  private get isInConference() {
    return this.router.url.startsWith("/a?");
  }

  get isAppointmentInteractionAvailable() {
    return (
      this.application.getActiveApplication()?.supportsInteractions() &&
      this.integration.supportsInteractionOrigin(
        ConversationOriginEnum.APPOINTMENT
      ) &&
      this.config.agentParamEnabled(AgentParam.SCHEDULE_ENABLED) &&
      this.config.agentParam(AgentParam.SCHEDULE_CHANNEL) ===
        ScheduleTypeEnum.appointment
    );
  }

  public notifyChange(data: IAppointment<IAppointmentRouting>) {
    this._appointmentChanged.next(data);
  }

  public query(
    query: PageableRequest
  ): Promise<IPagedResponse<IAppointment<IAppointmentRouting>>> {
    query.set("applicationId", this.application.getActiveApplication().getId());
    return this.appointmentResource
      .get({
        params: query.getParams(),
        urlPostfix: "/",
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public request(
    data: IScheduleAppointmentRequest
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .create(data, { urlPostfix: "/" })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public update(
    data: IScheduleAppointmentOptions
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .update(data, { urlPostfix: "/" })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   * Updates the state of the specified appointment and sends a related MQTT event
   * @param appointmentId Appointment id
   * @param state starting:CUSTOMER role, participantId required,  inprogress: CUSTOMER & AGENT roles, end: AGENT role
   * @returns 409: Conflict if another participant has already started the conversation and is on queue
   */
  public updateState(
    appointmentId: string,
    state: "starting" | "inprogress" | "end" | "failed",
    options?: {
      participantId?: string;
      systemMessage?: string;
      userMessage?: string;
    }
  ) {
    return this.appointmentResource
      .update(options, {
        urlPostfix: `/${appointmentId}/${state}`,
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public updateRouting(
    appointmentId: string,
    routing: IAppointmentRouting
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .update(
        { routing },
        {
          urlPostfix: `/${appointmentId}/interaction`,
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   * accepts incoming interaction as agent
   */
  public acceptInteraction(
    appointmentId: string
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .update(
        {
          participantId: this.user.getActiveUser().getId(),
        },
        {
          urlPostfix: `/${appointmentId}/accept`,
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public addParticipant(
    appointmentId: string,
    participant: IContactBase
  ): Promise<IScheduledAppointmentContact> {
    return this.appointmentResource
      .create(
        {
          id: appointmentId,
          customer: participant,
        },
        {
          urlPostfix: "/participants/customers/",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public updateParticipant(
    appointmentId: string,
    participantId: string,
    participant: IContactBase
  ): Promise<IScheduledAppointmentContact> {
    return this.appointmentResource
      .update(
        {
          customer: participant,
        },
        {
          urlPostfix: `/${appointmentId}/participants/${participantId}`,
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public updateParticipantState(
    appointmentId: string,
    participantState: AppointmentParticipantStateEnum
  ) {
    return this.appointmentResource
      .update(undefined, {
        urlPostfix: `/${appointmentId}/participants/${this.user.appointmentParticipantId}/state`,
        params: { status: participantState },
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public getViewState(
    appointment: IAppointment<IAppointmentRouting>
  ): AppointmentViewState {
    const customerState =
      appointment.participants.find((p) => p.type === "CUSTOMER")?.state ||
      "UNSET";

    const agentState =
      appointment.participants.find((p) => p.type === "AGENT")?.state ||
      "UNSET";

    switch (appointment.interaction.state) {
      case "EXPIRED":
        return "EXPIRED";
      case "FAILED":
        return "FAILED";
      case "STARTING":
        return "PENDING";
      case "PENDING":
        // check if end time passed
        return moment(appointment.start)
          .add(appointment.duration)
          .isBefore(moment())
          ? "EXPIRED"
          : "PENDING";

      case "COMPLETE":
        return "COMPLETED";
      case "IN_PROGRESS":
        // end time has passed
        if (
          moment().isAfter(moment(appointment.start).add(appointment.duration))
        ) {
          return customerState === "LEFT"
            ? agentState === "UNSET"
              ? "MISSED"
              : "ONGOING"
            : "OVERRUNING";
        } else if (
          // we are between start and end
          moment().isBetween(
            moment(appointment.start).subtract(1, "minute"),
            moment(appointment.start).add(appointment.duration)
          )
        ) {
          return "ONGOING";
        } else {
          return "ONGOING";
        }
    }
  }

  async complete(
    appointmentId: string
  ): Promise<IAppointment<IAppointmentRouting>> {
    try {
      const appointment = await this.get(appointmentId);
      // if appointment is still in progress or pending and the conversation ended
      // update the state
      if (
        ["IN_PROGRESS", "PENDING"].includes(appointment.interaction.state)
        // maybe another one created the appointment and I'm the one that answered it. so don't filter per owner
        // && appointment.participants.find(
        //   (p) => p.id === this.userService.getActiveUser().getId()
        // )
      ) {
        await this.updateState(appointment.id, "end");
        appointment.interaction.state = "COMPLETE";
        return appointment;
      }
    } catch (ex) {
      debugError(ex);
    }
  }

  public remove(id: string): Promise<void> {
    return this.appointmentResource.deleteById(id).catch((ex) => {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    });
  }

  public get(id: string): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource.getById(id).catch((ex) => {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    });
  }

  public removeParticipant(
    appointmentId: string,
    participantId: string
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .delete({
        urlPostfix: `${appointmentId}/participants/${participantId}`,
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public getParticipantDetails(
    appointmentId: string,
    participantId: string
  ): Promise<IScheduledAppointmentContact> {
    return this.appointmentResource
      .get({
        urlPostfix: `${appointmentId}/participants/${participantId}`,
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public addAgentToAppointment(
    id: string,
    userId: string
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .create(
        {
          id,
          userId,
        },
        {
          urlPostfix: "participants/agents/",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public addContactToAppointment(
    id: string,
    contact: IAppointmentContact
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .create(
        {
          id,
          customer: contact,
        },
        {
          urlPostfix: "participants/customers/",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public reassignAppointment(
    id: string,
    userId: string
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .update(
        {
          id,
          userId,
        },
        {
          urlPostfix: "reassign/",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public rescheduleAppointment(
    id: string,
    duration: string,
    start: Date,
    timezone: string,
    notifications: IAppointmentNotification[]
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .update(
        {
          id,
          duration,
          start,
          timezone,
          notifications,
        },
        {
          urlPostfix: "reschedule/",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public setAppointmentStateEnded(
    appointmentId: string
  ): Promise<IAppointment<IAppointmentRouting>> {
    return this.appointmentResource
      .update(null, {
        urlPostfix: `${appointmentId}/end`,
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public activate(appointmentId: string): Promise<{ url: string }> {
    return this.appointmentResource
      .create(null, {
        urlPostfix: `activate/${appointmentId}`,
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   *
   * @param applicationId current application id
   * @param code OTP you got from veriy
   * @returns 204 OK
   */
  public confirmSMSNotification(
    applicationId: string,
    code: string
  ): Promise<void> {
    return this.notificationResource
      .create(
        {
          applicationId,
          code,
        },
        {
          urlPostfix: "sms/confirm",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   *
   * @param applicationId current application id
   * @param code OTP you got from veriy
   * @returns 204 OK
   */
  public confirmEmailNotification(
    applicationId: string,
    code: string
  ): Promise<void> {
    return this.notificationResource
      .create(
        {
          applicationId,
          code,
        },
        {
          urlPostfix: "emails/confirm",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   * Sends an SMS with an OTP to be confirmed
   *
   * @param applicationId current application id
   * @param fromNumber Provisioned SMS number
   * @param toNumber testing device SMS number
   * @returns an OTP to be provided to "confirm" flow
   */
  public verifySMSNotification(
    applicationId: string,
    fromNumber: string,
    toNumber: string
  ): Promise<{ applicationId: string; validFor: string }> {
    return this.notificationResource
      .create(
        {
          applicationId,
          fromNumber,
          toNumber,
        },
        {
          urlPostfix: "sms/verify",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   * Sends an email with an OTP to be confirmed
   *
   * @param applicationId current application id
   * @param fromNumber Provisioned SMS number
   * @param toNumber testing device SMS number
   * @returns an OTP to be provided to "confirm" flow
   */
  public verifyEmailNotification(
    applicationId: string,
    domain: string,
    fromName: string,
    toEmail: string
  ): Promise<{ applicationId: string; validFor: string }> {
    return this.notificationResource
      .create(
        {
          applicationId,
          domain,
          fromName,
          toEmail,
        },
        {
          urlPostfix: "emails/verify",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public testSMSNotification(
    applicationId: string,
    toNumber: string,
    notification: IAppointmentNotification,
    locale: string,
    timezone: string
  ) {
    return this.notificationResource
      .create(
        {
          applicationId,
          toNumber,
          locale,
          timezone,
          ...notification,
        },
        {
          urlPostfix: "sms/test",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public testWebhookNotification(
    applicationId: string,
    notification: IAppointmentNotification,
    locale: string,
    timezone: string,
    baseUrl: string,
    subscription?: IWebhookSubscription
  ) {
    const body = {
      applicationId,
      timezone,
      locale,
      type: notification.type,
      period: notification.period,
      properties: notification.properties,
      baseUrl: baseUrl,
      authentication:
        subscription?.authentication ||
        WebhookSubscriptionAuthenticationEnum.NONE,
      oAuth2ClientCredentials: undefined,
      basicAuth: undefined,
    };
    switch (subscription?.authentication) {
      case WebhookSubscriptionAuthenticationEnum.OAUTH2_CLIENT_CREDENTIALS:
        body.oAuth2ClientCredentials = (
          subscription as IWebhookSubscriptionClientCredentials
        ).oAuth2ClientCredentials;
        break;
      case WebhookSubscriptionAuthenticationEnum.HTTP_BASIC:
        body.basicAuth = (
          subscription as IWebhookSubscriptionBasicAuth
        ).basicAuth;
    }

    return this.notificationResource
      .create(body, { urlPostfix: "webhooks/test" })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public testEmailNotification(
    applicationId: string,
    toEmail: string,
    notification: IAppointmentNotification,
    locale: string,
    timezone: string
  ) {
    return this.notificationResource
      .create(
        {
          applicationId,
          toEmail,
          locale,
          timezone,
          ...notification,
        },
        {
          urlPostfix: "emails/test",
        }
      )
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public async getTagFormats(): Promise<IAppointmentFormat[]> {
    if (this.tagFormats?.length === 0) {
      try {
        const page = await this.notificationResource
          .get({
            urlPostfix: "tags",
          })
          .catch((ex) => {
            this.errorHandler.handleNotAuthenticatedError(ex);
            throw ex;
          });
        this.tagFormats = page.content;
      } catch (ex) {}
    }
    return this.tagFormats;
  }

  public async getSupportedLocales(): Promise<IAppointmentLocale[]> {
    if (this.supportedLocales?.length === 0) {
      try {
        const page = await this.notificationResource
          .get({
            urlPostfix: "locales",
          })
          .catch((ex) => {
            this.errorHandler.handleNotAuthenticatedError(ex);
            throw ex;
          });
        this.supportedLocales = page.content;
      } catch (ex) {}
    }
    return this.supportedLocales;
  }

  public async getWebhookTypes(): Promise<
    AppointmentWebhookNotificationTypeEnum[]
  > {
    if (this.webhookTypes?.length === 0) {
      try {
        const response = await this.notificationResource
          .get({
            urlPostfix: "webhooks/types",
          })
          .catch((ex) => {
            this.errorHandler.handleNotAuthenticatedError(ex);
            throw ex;
          });
        this.webhookTypes = response.types;
      } catch (ex) {}
    }
    return this.webhookTypes;
  }

  public propagateEvent(event: {
    eventId: string;
    type: AppointmentEventType;
    appointmentId: string;
    timestamp: string;
  }) {
    switch (event.type) {
      case "APAgentJoinedEvent":
      case "APAgentLeftEvent":
      case "APCustomerJoinedEvent":
      case "APCustomerLeftEvent":
      case "APCustomerWaitingEvent":
        debug("appointment participant state changed", event);
        this._appointmentParticipantStateChanged.next(event);
        break;

      case "APRescheduledEvent":
      case "APReassignedEvent":
      case "APInProgressEvent":
      case "APCompletedEvent":
      case "APDeletedEvent":
      case "APExpiredEvent":
      case "APUpdatedEvent":
      case "APFailedEvent":
        debug("appointment state changed", event);
        this._appointmentStateChanged.next(event);
        break;
      case "APScheduledEvent":
        debug("appointment created", event);
        this._appointmentCreated.next(event.appointmentId);
        break;
    }
  }

  public isNotificationMutliple(
    type:
      | AppointmentNotificationTypeEnum
      | AppointmentWebhookNotificationTypeEnum
  ) {
    return [
      AppointmentNotificationTypeEnum.room,
      AppointmentNotificationTypeEnum.reminder,
      AppointmentWebhookNotificationTypeEnum.reminder,
    ].includes(type);
  }

  public isNotificationInstant(
    type:
      | AppointmentNotificationTypeEnum
      | AppointmentWebhookNotificationTypeEnum
  ) {
    return [
      AppointmentNotificationTypeEnum.create,
      AppointmentNotificationTypeEnum.update,
      AppointmentNotificationTypeEnum.delete,
      AppointmentNotificationTypeEnum.reschedule,
      AppointmentWebhookNotificationTypeEnum.cancelled,
      AppointmentWebhookNotificationTypeEnum.completed,
      AppointmentWebhookNotificationTypeEnum.created,
      AppointmentWebhookNotificationTypeEnum.inProgress,
      AppointmentWebhookNotificationTypeEnum.interactionUpdated,
      AppointmentWebhookNotificationTypeEnum.noShow,
      AppointmentWebhookNotificationTypeEnum.participantAdded,
      AppointmentWebhookNotificationTypeEnum.participantRemoved,
      AppointmentWebhookNotificationTypeEnum.participantUpdated,
      AppointmentWebhookNotificationTypeEnum.rescheduled,
      AppointmentWebhookNotificationTypeEnum.reassigned,
      AppointmentWebhookNotificationTypeEnum.updated,
    ].includes(type);
  }

  public getNotificationLogs(
    appointmentId: string,
    query: AppointmentLogRequestBuilder
  ): Promise<IPagedResponse<IAppointmentNotificationLog>> {
    return this.appointmentResource
      .get({
        params: query.build().getParams(),
        urlPostfix: appointmentId + "/notifications",
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public getNotificationLogEvents(
    appointmentId: string,
    query: AppointmentLogEventRequestBuilder
  ): Promise<IPagedResponse<IAppointmentNotificationLogEvent>> {
    return this.appointmentResource
      .get({
        params: query.build().getParams(),
        urlPostfix: appointmentId + "/notifications/events",
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  public retryNotification(
    appointmentNotificationLogId: string
  ): Promise<IAppointmentNotificationLog> {
    return this.notificationResource
      .update(undefined, {
        urlPostfix: appointmentNotificationLogId + "/retry",
      })
      .catch((ex) => {
        this.errorHandler.handleNotAuthenticatedError(ex);
        throw ex;
      });
  }

  /**
   * only ROLE_ADMIN can call it. Trigger cache invalidation to apply changes instantly instead of having to wait up to 30 secs
   */
  public clearCache() {
    if (
      this.config.agentParamEnabled(AgentParam.SCHEDULE_ENABLED) &&
      this.integration.supportsInteractionOrigin(
        ConversationOriginEnum.APPOINTMENT
      )
    ) {
      return this.appointmentResource
        .create(null, {
          urlPostfix: `applications/${this.application
            .getActiveApplication()
            .getId()}/invalidate`,
        })
        .catch((ex) => {
          this.errorHandler.handleNotAuthenticatedError(ex);
          throw ex;
        });
    }
  }
}
